import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { AddToSlackButton } from '@/components/global/InstallSlack';
import { useFeatureFlags } from '@/components/providers/FeatureFlagsProvider';
import { awsCredentialsQueries } from '@/lib/queries/awsCredentials';
import { awsRoleQueries } from '@/lib/queries/awsRole';
import { githubConfigQueries } from '@/lib/queries/githubConfig';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { terraformBackendQueries } from '@/lib/queries/terraformBackend';

import { Catalog } from './-components/Catalog';
import { getCatalog, getRequirements } from './-data';

export const Route = createFileRoute('/_root/catalog/')({
  component: RouteComponent,
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });
    context.queryClient.prefetchQuery(
      githubConfigQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(
      awsCredentialsQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(awsRoleQueries.list(defaultProject.id));

    return defaultProject;
  },
});

function RouteComponent() {
  const defaultProject = Route.useLoaderData();
  const { data: githubInstall } = useQuery(
    githubConfigQueries.list(defaultProject.id),
  );

  const { data: awsUsers } = useQuery(
    awsCredentialsQueries.list(defaultProject.id),
  );

  const { data: awsRoles } = useQuery(awsRoleQueries.list(defaultProject.id));

  const { data: buckets } = useQuery(
    terraformBackendQueries.listBuckets(defaultProject.id),
  );

  const catalogMap = useMemo(() => getCatalog(), []);
  const requirementsMap = useMemo(
    () =>
      getRequirements({
        awsCredentials: awsUsers,
        awsRoles,
        githubInstalls: githubInstall,
        s3Buckets: buckets,
      }),
    [awsUsers, awsRoles, githubInstall, buckets],
  );

  const { FeatureAskAnnie } = useFeatureFlags();
  if (FeatureAskAnnie !== true) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <h1 className="text-3xl mb-6 font-medium">
        <FormattedMessage id="catalog.title" />
      </h1>
      <h3 className="text-sm mb-6 font-normal">
        <FormattedMessage id="catalog.subtitle" values={{ br: <br /> }} />
      </h3>
      <div className="flex flex-row justify-between items-baseline mb-2 mt-6">
        <h2 className="text-2xl font-normal">
          <FormattedMessage id="catalog.tryIt" />
        </h2>
        <AddToSlackButton variant="main" />
      </div>
      <Catalog catalog={catalogMap} requirements={requirementsMap} />
    </>
  );
}
